import { getArtworksPerCategory } from '../api/artCollection.api'
import { type ArtCatalog } from '../pages/ArtCollection/ArtCollection.types'
import sessionStorageManager from './SessionStorageManager'
import shuffle from 'lodash/shuffle'
import groupBy from 'lodash/groupBy'
import { type Artwork } from '../types/art.types'
import { logger } from './logger'
import { experimentManager } from './ExperimentManager/ExperimentManager'
import partition from 'lodash/partition'
import { getUrlParam } from 'utils/UrlUtils'

const FALLBACK_CATEGORY_NAMES = [
  'Best Sellers',
  'Sayings',
  'Abstract',
  'Beach',
  'Botanical',
  'Nature',
  'Kids',
  'Travel',
  'Kitchen',
  'Figurative',
  'Classic Art',
]

// Save catalog in session storage to keep the randomized order between page refreshes
const ART_CATALOG_KEY = 'art-collection-catalog'
const ARTWORKS_PER_CATEGORY_LIMIT = 40
const FIRST_CATEGORY_URL_PARAM = 'category'

export function getArtCatalog(): ArtCatalog {
  return sessionStorageManager.get(ART_CATALOG_KEY) as ArtCatalog
}

// Map old category names to new category names
const REDIRECT_CATEGORY: Record<string, string> = {
  Plants: 'Botanical',
  Flowers: 'Botanical',
  Animals: 'Nature',
  Mountains: 'Nature',
  Urban: 'Travel',
  Lines: 'Abstract',
}

export function getCollectionCategoryFromUrl(): string | null {
  const categoryName = getUrlParam(FIRST_CATEGORY_URL_PARAM)
  if (categoryName) {
    const escapedName = categoryName.replace(/_/g, ' ')
    return REDIRECT_CATEGORY[escapedName] || escapedName
  }
  return null
}

// Show artworks with the same set name first. Artworks without set names are shown last.
function groupArtworksBySetName(artworks: Artwork[]): Artwork[] {
  const [withSetName, withoutSetName] = partition(
    artworks,
    (artwork) => artwork.artworkSetNames && artwork.artworkSetNames.length > 0
  )
  const groupBySetName = groupBy(withSetName, 'artworkSetNames[0]')
  const sortedGroupBySetName = shuffle(Object.values(groupBySetName))
    .sort((a, b) => (a.length % 2) - (b.length % 2)) // Sort even length sets first - so artworks of the same set appear together
    .map((artworks) => artworks.sort((a, b) => a.title.localeCompare(b.title)))
    .flat()
  return [...sortedGroupBySetName, ...shuffle(withoutSetName)]
}

const randomizeCatalog = (
  categories: {
    id: string
    name: string
    artworks: Artwork[]
  }[],
  pinnedCategoryNames: string[]
) => {
  const firstCategoryName = getCollectionCategoryFromUrl()
  const firstCategory = categories.find(
    (category) => category.name === firstCategoryName
  )
  if (firstCategory) {
    pinnedCategoryNames = [
      firstCategory.name,
      ...pinnedCategoryNames.filter((name) => name !== firstCategory.name),
    ]
  }

  // Divide categories into pinned and unpinned
  const [pinned, unpinned] = partition(categories, (category) =>
    pinnedCategoryNames.includes(category.name)
  )

  // Sort pinned categories in the order they appear in pinnedCategoryNames
  const pinnedCategories = pinned.sort(
    (a, b) =>
      pinnedCategoryNames.indexOf(a.name) - pinnedCategoryNames.indexOf(b.name)
  )
  const unpinnedCategories = shuffle(unpinned)

  // Shuffle the artworks in each category
  const orderedCategories = [...pinnedCategories, ...unpinnedCategories]
  const shuffledCatalog: ArtCatalog = {}
  orderedCategories.forEach((category) => {
    shuffledCatalog[category.name] = groupArtworksBySetName(category.artworks)
  })
  return shuffledCatalog
}

// Reset catalog if the first category in the URL is different from the first category in the catalog
function shouldResetCatalog(catalog: ArtCatalog): boolean {
  const urlCategory = getCollectionCategoryFromUrl()
  if (urlCategory) {
    return urlCategory !== Object.keys(catalog)[0]
  }
  return false
}

export async function initArtCatalog(): Promise<ArtCatalog | null> {
  const catalog = getArtCatalog()
  if (catalog && !shouldResetCatalog(catalog)) {
    return catalog
  } else {
    const payload =
      experimentManager.getVariantPayload('art-collection-29-jan-2025') || {}
    const { categories, pinnedCategories, variant, limit } = payload
    const categoryNames =
      categories && categories?.length > 0
        ? categories
        : FALLBACK_CATEGORY_NAMES
    const apiPerCategory = await getArtworksPerCategory({
      categoryNames,
      limit: limit || ARTWORKS_PER_CATEGORY_LIMIT,
      variant,
    })
    if (apiPerCategory.list.length > 0) {
      const newCatalog = randomizeCatalog(
        apiPerCategory.list,
        pinnedCategories || []
      )
      sessionStorageManager.set(ART_CATALOG_KEY, newCatalog)
      logger.info('Completed init art catalog')
      return newCatalog
    } else {
      logger.warning('Failed to init art catalog')
      return null
    }
  }
}
